import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from 'react'
import { createPortal } from 'react-dom'
import {
  autoUpdate,
  flip,
  hide,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom'
import { Check, ChevronDown, Search, Star, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  groupImplantCatalogOptions,
  matchesImplantCatalogOption,
  type ImplantCatalogOption,
  type ImplantCatalogOptionGroup,
} from './implantCatalogOptions'

type RenderedImplantOption = ImplantCatalogOption & {
  isHistorical?: boolean
}

type RenderedImplantGroup = Omit<ImplantCatalogOptionGroup, 'key' | 'options'> & {
  key: ImplantCatalogOptionGroup['key'] | 'historical'
  options: RenderedImplantOption[]
}

type ImplantCatalogComboboxProps = {
  id: string
  value: string
  items: readonly ImplantCatalogOption[] | undefined
  onValueChange: (value: string) => void
  disabled?: boolean
  loading?: boolean
  invalid?: boolean
  describedBy?: string
  searchLabel: string
}

export function ImplantCatalogCombobox({
  id,
  value,
  items,
  onValueChange,
  disabled = false,
  loading = false,
  invalid = false,
  describedBy,
  searchLabel,
}: ImplantCatalogComboboxProps) {
  const generatedId = useId()
  const listboxId = `${generatedId}-implant-options`
  const wrapperRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const optionRefs = useRef(new Map<string, HTMLButtonElement>())
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeId, setActiveId] = useState<string | null>(null)

  const {
    refs: floatingRefs,
    floatingStyles,
    middlewareData,
    placement,
    isPositioned,
  } = useFloating({
    open,
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({ fallbackStrategy: 'bestFit', padding: 12 }),
      shift({ crossAxis: true, padding: 12 }),
      size({
        padding: 12,
        apply({ availableHeight, availableWidth, elements, rects }) {
          const panelWidth = Math.min(
            Math.max(rects.reference.width, 544),
            availableWidth
          )
          Object.assign(elements.floating.style, {
            width: `${Math.max(0, panelWidth)}px`,
            maxHeight: `${Math.max(0, Math.min(432, availableHeight))}px`,
          })
        },
      }),
      hide({ padding: 12, strategy: 'referenceHidden' }),
    ],
  })

  const setTriggerElement = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node
      floatingRefs.setReference(node)
    },
    [floatingRefs]
  )

  const setPanelElement = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node
      floatingRefs.setFloating(node)
    },
    [floatingRefs]
  )

  const selectedItem = items?.find((item) => item._id === value)
  const historicalItem = useMemo<RenderedImplantOption | null>(() => {
    if (!value || items === undefined || selectedItem) return null
    return {
      _id: value,
      name: 'Impianto storico non più attivo',
      brand: 'Selezione salvata in precedenza',
      isHistorical: true,
    }
  }, [items, selectedItem, value])

  const groups = useMemo<RenderedImplantGroup[]>(() => {
    const rankedGroups: RenderedImplantGroup[] = groupImplantCatalogOptions(
      items ?? [],
      searchTerm
    )
    const showHistorical =
      historicalItem && matchesImplantCatalogOption(historicalItem, searchTerm)

    return [
      ...(showHistorical
        ? [
            {
              key: 'historical' as const,
              label: 'Selezione storica',
              options: [historicalItem],
            },
          ]
        : []),
      ...rankedGroups,
    ]
  }, [historicalItem, items, searchTerm])

  const flatOptions = useMemo(
    () => groups.flatMap((group) => group.options),
    [groups]
  )
  const flatOptionKey = flatOptions.map((option) => option._id).join('|')
  const activeIndex = flatOptions.findIndex((option) => option._id === activeId)
  const activeOptionDomId =
    activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
  const selectedDisplay = selectedItem ?? historicalItem
  const isControlDisabled = disabled || loading

  const closePanel = (restoreFocus = false) => {
    setOpen(false)
    setSearchTerm('')
    setActiveId(null)
    if (restoreFocus) {
      requestAnimationFrame(() => triggerRef.current?.focus())
    }
  }

  const openPanel = (initialPosition: 'selected' | 'first' | 'last' = 'selected') => {
    if (isControlDisabled) return
    const unfiltered = groupImplantCatalogOptions(items ?? [], '').flatMap(
      (group) => group.options
    )
    const optionsWithHistorical = historicalItem
      ? [historicalItem, ...unfiltered]
      : unfiltered
    const initialOption =
      initialPosition === 'last'
        ? optionsWithHistorical[optionsWithHistorical.length - 1]
        : initialPosition === 'first'
          ? optionsWithHistorical[0]
          : optionsWithHistorical.find((option) => option._id === value) ??
            optionsWithHistorical[0]

    setSearchTerm('')
    setActiveId(initialOption?._id ?? null)
    setOpen(true)
  }

  const selectOption = (option: RenderedImplantOption) => {
    onValueChange(option._id)
    closePanel(true)
  }

  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => searchRef.current?.focus())
  }, [open])

  useEffect(() => {
    if (!open) return
    const activeStillExists = flatOptions.some((option) => option._id === activeId)
    if (activeStillExists) return
    const selectedStillExists = flatOptions.find((option) => option._id === value)
    setActiveId(selectedStillExists?._id ?? flatOptions[0]?._id ?? null)
  }, [activeId, flatOptionKey, flatOptions, open, value])

  useEffect(() => {
    if (!open || !activeId) return
    optionRefs.current.get(activeId)?.scrollIntoView({ block: 'nearest' })
  }, [activeId, open])

  useEffect(() => {
    if (!open) return
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      const isInsideTrigger = wrapperRef.current?.contains(target)
      const isInsidePanel = panelRef.current?.contains(target)
      if (!isInsideTrigger && !isInsidePanel) closePanel()
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [open])

  useEffect(() => {
    if (isControlDisabled && open) closePanel()
  }, [isControlDisabled, open])

  const moveActive = (direction: 1 | -1) => {
    if (flatOptions.length === 0) return
    const nextIndex =
      activeIndex < 0
        ? direction === 1
          ? 0
          : flatOptions.length - 1
        : (activeIndex + direction + flatOptions.length) % flatOptions.length
    setActiveId(flatOptions[nextIndex]._id)
  }

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveActive(1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveActive(-1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      setActiveId(flatOptions[0]?._id ?? null)
    } else if (event.key === 'End') {
      event.preventDefault()
      setActiveId(flatOptions[flatOptions.length - 1]?._id ?? null)
    } else if (event.key === 'Enter') {
      const activeOption = flatOptions[activeIndex]
      if (activeOption) {
        event.preventDefault()
        selectOption(activeOption)
      }
    } else if (event.key === 'Escape') {
      event.preventDefault()
      closePanel(true)
    } else if (event.key === 'Tab') {
      closePanel()
    }
  }

  const handleFocusLeaving = (event: FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget
    if (
      nextTarget instanceof Node &&
      (wrapperRef.current?.contains(nextTarget) || panelRef.current?.contains(nextTarget))
    ) {
      return
    }
    closePanel()
  }

  return (
    <div ref={wrapperRef} className="relative" onBlurCapture={handleFocusLeaving}>
      <Button
        ref={setTriggerElement}
        id={id}
        type="button"
        variant="outline"
        disabled={isControlDisabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        onClick={() => (open ? closePanel() : openPanel())}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            openPanel(event.key === 'ArrowUp' ? 'last' : 'first')
          }
        }}
        className={cn(
          'h-auto min-h-11 w-full justify-between gap-3 whitespace-normal bg-white px-3 py-2 text-left hover:bg-white',
          invalid && 'border-red-500 focus-visible:ring-red-500'
        )}
      >
        <span className="min-w-0 flex-1">
          {selectedDisplay ? (
            <>
              <span className="block text-sm font-medium leading-5 text-gray-950">
                {selectedDisplay.name}
              </span>
              <span className="mt-0.5 block text-xs font-normal leading-4 text-gray-600">
                {selectedDisplay.brand}
                {selectedDisplay.source_code
                  ? ` · Codice ${selectedDisplay.source_code}`
                  : ''}
              </span>
            </>
          ) : (
            <span className="block truncate text-sm font-normal text-gray-500">
              {loading ? 'Caricamento catalogo…' : 'Cerca e seleziona un impianto…'}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-gray-500 transition-transform duration-150',
            open && 'rotate-180'
          )}
          aria-hidden="true"
        />
      </Button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={setPanelElement}
          style={{
            ...floatingStyles,
            visibility:
              isPositioned && !middlewareData.hide?.referenceHidden
                ? 'visible'
                : 'hidden',
          }}
          data-placement={placement}
          className="z-[100] flex max-h-[min(27rem,calc(100dvh-1.5rem))] flex-col overflow-hidden rounded-xl bg-white shadow-[0_18px_48px_rgba(15,23,42,0.2)] ring-1 ring-gray-200"
          onBlurCapture={handleFocusLeaving}
        >
          <div className="shrink-0 border-b border-gray-100 bg-white p-3">
            <div className="mb-2 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-950">Scegli l&apos;impianto</p>
                <p className="mt-0.5 text-xs text-gray-600">
                  Cerca per nome, marca o codice articolo.
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => closePanel(true)}
                aria-label="Chiudi il catalogo impianti"
                className="-mr-1 -mt-1 h-9 w-9 shrink-0 p-0 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                aria-hidden="true"
              />
              <Input
                ref={searchRef}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Cerca per nome, marca o codice…"
                autoComplete="off"
                role="combobox"
                aria-label={searchLabel}
                aria-autocomplete="list"
                aria-expanded="true"
                aria-controls={listboxId}
                aria-activedescendant={activeOptionDomId}
                className="pl-9"
              />
            </div>
            <p className="mt-2 text-xs font-medium text-gray-600" role="status" aria-live="polite">
              {flatOptions.length === 1
                ? '1 impianto trovato'
                : `${flatOptions.length} impianti trovati`}
            </p>
          </div>

          <div
            id={listboxId}
            role="listbox"
            aria-label="Catalogo impianti"
            className="custom-scrollbar min-h-0 flex-1 scroll-pt-8 overflow-y-auto overscroll-contain py-1"
          >
            {flatOptions.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm font-medium text-gray-800">
                  Nessun impianto trovato
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Prova con un altro nome, marca o codice.
                </p>
              </div>
            ) : (
              groups.map((group) => {
                const headingId = `${listboxId}-${group.key}-heading`
                return (
                  <div key={group.key} role="group" aria-labelledby={headingId}>
                    <div
                      id={headingId}
                      className="sticky top-0 z-10 flex items-center gap-1.5 bg-gray-50/95 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-600"
                    >
                      {group.key === 'favorites' && (
                        <Star className="h-3.5 w-3.5 fill-primary text-primary" aria-hidden="true" />
                      )}
                      {group.label}
                    </div>
                    {group.options.map((option) => {
                      const optionIndex = flatOptions.findIndex(
                        (candidate) => candidate._id === option._id
                      )
                      const isActive = option._id === activeId
                      const isSelected = option._id === value
                      return (
                        <button
                          key={option._id}
                          ref={(node) => {
                            if (node) optionRefs.current.set(option._id, node)
                            else optionRefs.current.delete(option._id)
                          }}
                          id={`${listboxId}-option-${optionIndex}`}
                          type="button"
                          role="option"
                          tabIndex={-1}
                          aria-selected={isSelected}
                          onMouseDown={(event) => event.preventDefault()}
                          onMouseEnter={() => setActiveId(option._id)}
                          onClick={() => selectOption(option)}
                          className={cn(
                            'flex min-h-14 w-full scroll-mt-8 items-start gap-3 px-4 py-3 text-left outline-hidden transition-colors',
                            isActive ? 'bg-orange-50' : 'hover:bg-gray-50'
                          )}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="flex items-start gap-1.5">
                              <span className="text-sm font-semibold leading-5 text-gray-950">
                                {option.name}
                              </span>
                              {option.is_favorite && (
                                <>
                                  <Star
                                    className="h-3.5 w-3.5 shrink-0 fill-primary text-primary"
                                    aria-hidden="true"
                                  />
                                  <span className="sr-only">Preferito</span>
                                </>
                              )}
                            </span>
                            <span className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-xs leading-4 text-gray-600">
                              <span>{option.brand}</span>
                              {option.source_code
                                ? <span className="font-medium text-gray-700">Codice {option.source_code}</span>
                                : ''}
                            </span>
                          </span>
                          <Check
                            className={cn(
                              'mt-1 h-4 w-4 shrink-0 text-primary',
                              !isSelected && 'invisible'
                            )}
                            aria-hidden="true"
                          />
                        </button>
                      )
                    })}
                  </div>
                )
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
